// The aircraft catalog: which art/ model flies each class, and the rig that
// mounts it on a PlaneVisual (model-integration.md, Parts 3 and 4).
//
// Roster decision (Part 4.1, 2026-09-23): option A — one model per class, and
// both teams fly it. Paint decision (2026-09-25): both teams fly it in the
// same factory finish. A Spitfire is dark green and ocean grey whoever's
// strapped in, a Mustang bare metal, an Avenger Navy blue; the art's team
// marks (spinner, wingtips, rudder) wear each airframe's own trim — the
// Spitfire's Sky, an olive-drab anti-glare, insignia white — never Crimson
// or Cobalt. Whose side a plane is on is the pilot's nameplate's job, a tile
// flown in the team color (planes.js, makeNameplate), and yours shows no side
// at all: you know which team you're on.
//
// A rig is purely a view. It never changes where a round lands (that is
// hitbox.go's job, rebuilt from these same node bounds), where a turret fires
// from (config turrets[].pos, which mirror the pivots below), or how anything
// flies. Everything here is either lifted straight from the manifest in
// art/model-integration.md or derived from the model's own node bounds at load.

import * as THREE from "three";
import {
  loadModel,
  loadDamageKit,
  modelIfReady,
  ModelInstance,
  modelsEnabled,
  setModelOptions,
  paintMaterialDS,
} from "./models.js";
import { uploadInstances } from "./instance_upload.js";

// Per model: node prefix, propellers (spin direction: +1 / -1 for handed
// twins), muzzles in body axes (nose +X, up +Y, starboard +Z), contrail geometry,
// jet nozzles, crewable turrets, the level-of-detail distances, and `trim` —
// the paint the art's `*_Team` marks wear instead of a team hue (models.js).
// Each trim comes off the airframe's own palette, and none is red, blue or
// the ace's gold: the art authored those marks in placeholder hues (the P-38's
// and the Avenger's are Crimson red, every jet's is Cobalt blue exactly).
//
// Muzzles are the model's own barrel tips (measured off the art), trimmed or
// padded to the flash count the class's config battery fires — a quad-fifty
// class flashes four barrels whatever the airframe carries, so the sight
// picture matches the stream the server is actually throwing.
export const AIRCRAFT_MODELS = {
  // The Spitfire is painted, not trimmed, like the Bf 109 below: its art
  // carries a day fighter's marks as their own materials (art/liverylib_spitfire
  // — the Sky spinner and band, the yellow down the outer leading edges) and
  // the factory finish shows its Dark Green disruptive over the Ocean Grey
  // (`livery`). The PAINT SHOP's YQ-P (config.schemes, `model: "spitfire"`)
  // adds 616 Squadron's codes and a summer's wear. The trim (Sky) is left for
  // the wreck's char.
  spitfire: {
    prefix: "SP_",
    trim: 0xc9d6a8, // Sky: the spinner and tail band of the real thing
    livery: "Factory",
    camo: 0x434b36, // the Dark Green, mixed into LOD1 and LOD2's uppers where the decals stop
    props: [{ node: "Propeller", dir: 1 }],
    // Two Hispanos in the wing, then the outboard pair the quad battery adds.
    muzzles: [[1.55, -0.235, 1.95], [1.55, -0.235, -1.95], [1.42, -0.22, 2.9], [1.42, -0.22, -2.9]],
    trails: { nacelles: 1.8, tip: 5.58 },
  },
  // The Mustang is painted too (art/liverylib_p51): its bare metal is split
  // into uppers, flanks and belly, and the old trim into the spinner, nose
  // ring, rudder and wingtips, so every scheme repaints it. The factory finish
  // is Pattie Ann (`livery`), the 352nd's blue-nosed P-51B off the plate:
  // invasion stripes, PE-T, the red fin cap. The PAINT SHOP's Shangri-La
  // (Gentile's olive drab, the red checks and the eagle) and Sunshine (Banks'
  // black-banded Fifth Air Force Mustang) bring their own. The trim (olive
  // drab) is left for the wreck's char.
  p51d: {
    prefix: "P51_",
    trim: 0x4b5130, // olive drab, off the anti-glare panel ahead of the canopy
    livery: "PattieAnn",
    finish: "Pattie Ann", // what the PAINT SHOP calls the factory chip on a Mustang
    props: [{ node: "Propeller", dir: 1 }],
    // Six fifties in the wings; the 20 mm pair doctrine flashes the inboard two.
    muzzles: [[1.2, -0.18, 2.35], [1.2, -0.18, -2.35], [1.18, -0.16, 2.6], [1.18, -0.16, -2.6], [1.16, -0.14, 2.85], [1.16, -0.14, -2.85]],
    trails: { nacelles: 1.8, tip: 5.66 },
  },
  // The Lightning is painted too (art/liverylib_p38): its bare metal split
  // into uppers, flanks and belly on the gondola and both booms, the old trim
  // into the nose cap, rudders, wingtips and four spinner rings. The factory
  // finish is Mama's Boy (`livery`), the 55th FS's P-38J off the plate:
  // yellow spinners and cowl rings, invasion stripes round both booms and
  // across the outer wings. The PAINT SHOP's Putt Putt Maru (MacDonald's
  // silver 100 in the 475th's red, yellow and blue) and Frances' Rebel (the
  // 459th's olive drab with a dragon down each boom) bring their own. The
  // trim (olive drab) is left for the wreck's char.
  p38: {
    prefix: "P38_",
    trim: 0x4b5130, // olive drab, off the anti-glare panels
    livery: "MamasBoy",
    finish: "Mama's Boy", // what the PAINT SHOP calls the factory chip on a Lightning
    // Handed props: the pair turns outward, as Lockheed built them.
    props: [{ node: "Propeller_L", dir: -1 }, { node: "Propeller_R", dir: 1 }],
    // The lone heavy cannon first (the class fires one barrel), then the nose MGs.
    muzzles: [[3.55, 0.12, 0], [3.62, 0.3, 0.07], [3.62, 0.3, -0.07], [3.62, 0.2, 0.17], [3.62, 0.2, -0.17]],
    trails: { nacelles: 2.5, tip: 7.97 },
  },
  b17: {
    prefix: "B17_",
    trim: 0xe8e6dc, // insignia white: the group marks on the fin and cowl rings
    props: [
      { node: "Propeller_InL", dir: 1 },
      { node: "Propeller_InR", dir: 1 },
      { node: "Propeller_OutL", dir: 1 },
      { node: "Propeller_OutR", dir: 1 },
    ],
    // The chin turret's twin fifties.
    muzzles: [[9.6, -1.0, 0.12], [9.6, -1.0, -0.12]],
    // Four Cyclones, four contrails: the inboard pair's props, then the outboard's.
    trails: { nacelles: 3.5, outboard: 7.4, tip: 15.85 },
    // Crew seats in config order (B-17: Tail Gunner, Top Gunner). `aft`
    // turrets are built facing −X, so their node yaw 0 is the stowed-aft pose.
    // `tip` is where the twin barrels end, in the turret's own frame.
    turrets: [
      { node: "TailGun", aft: true, tip: [1.15, 0.0, 0.1] },
      { node: "TopTurret", aft: false, tip: [1.5, 0.36, 0.13] },
    ],
    // The ball and chin turrets aren't crewed: they ride stowed.
  },
  tbf_avenger: {
    prefix: "TBF_",
    trim: 0xd8dcdc, // the Navy scheme's own white
    props: [{ node: "Propeller", dir: 1 }],
    // The wing fifties, each flashing as a pair for the class's quad battery.
    muzzles: [[2.1, -0.36, 3.0], [2.1, -0.36, -3.0], [2.02, -0.35, 3.24], [2.02, -0.35, -3.24]],
    trails: { nacelles: 1.8, tip: 8.26 },
  },
  f104: {
    prefix: "F104_",
    trim: 0xe8e6dc, // insignia white tip tanks and fin band
    props: [],
    jet: true,
    nozzles: [[-9.0, 0.06, 0]],
    // The M61 port on the lower left of the nose.
    muzzles: [[4.6, -0.3, -0.6]],
    trails: { nacelles: 0.6, tip: 3.8 },
  },
  skywriter: {
    prefix: "SKY_",
    props: [{ node: "Propeller", dir: 1 }],
    muzzles: [],
    trails: { nacelles: 0.8, tip: 5.0 },
    towHook: [-4.4, -0.05, 0],
    teamless: true,
  },
  marquee: {
    prefix: "MQ_",
    props: [{ node: "Prop_L", dir: -1 }, { node: "Prop_R", dir: 1 }],
    muzzles: [],
    trails: { nacelles: 2.4, tip: 3.0 },
    // The envelope's radius, which the flank signboards sit proud of.
    envelopeR: 3.35,
    teamless: true,
  },
  // --- the rest of the hangar (classes 8–16) ---------------------------------
  // The other Luftwaffe types' authored `_Team` marks are RLM 04 yellow, a shade off
  // the ace's gold spinner, so they wear the white of a Mediterranean fuselage
  // band instead; the Allied ones wear their own insignia white.
  // The Bf 109 is painted, not trimmed: its art carries the real markings as
  // their own materials (art/liverylib — the RLM 70 spinner, the RLM 04 chin,
  // band and wingtip undersides of an Eastern Front Gustav) and the factory
  // finish shows its 75 mottle (`livery`). The PAINT SHOP's Galland and
  // Trautloft liveries (config.schemes, `model: "bf109"`) repaint those and
  // swap in their own decals. The trim is left for the wreck's char.
  bf109: {
    prefix: "BF_",
    trim: 0xe8e6dc,
    livery: "Factory",
    props: [{ node: "Propeller", dir: 1 }],
    // The hub cannon down the spinner (the class fires one barrel), then the
    // cowl MGs.
    muzzles: [[3.99, -0.01, 0], [3.18, 0.45, 0.12], [3.18, 0.45, -0.12]],
    trails: { nacelles: 1.8, tip: 4.98 },
  },
  // The Fw 190 is painted too (art/liverylib_fw190), as the 109 is: its old
  // trim split into the band, rudder, wingtip caps and a spinner cut along a
  // spiral, its cowling into top, sides and chin. The factory finish is
  // Kittel's White 1 (`livery`), the 2./JG 54 A-5 off the plate: 74/75
  // splinter over a dappled 76, the Eastern Front's yellow chin, band, tail
  // and wingtip undersides, a white spiral on the black spinner. The PAINT
  // SHOP's Romm's D-9 (the black-green long nose, the Kommandeur's chevrons
  // in a white line) and JG 301's Yellow 5 (a Ta 152's black-green over grey,
  // the yellow-and-red band, a red spiral spinner) bring their own.
  // The trim is left for the wreck's char.
  fw190: {
    prefix: "FW_",
    trim: 0xe8e6dc,
    livery: "Kittel",
    finish: "Kittel's White 1", // what the PAINT SHOP calls the factory chip on a Fw 190
    props: [{ node: "Propeller", dir: 1 }],
    // The outer wing cannon pair the class's battery fires, then the root pair.
    muzzles: [[1.49, -0.23, 2.3], [1.49, -0.23, -2.3], [1.53, -0.38, 0.75], [1.53, -0.38, -0.75]],
    trails: { nacelles: 1.8, tip: 5.27 },
  },
  p40n: {
    prefix: "P40_",
    trim: 0xf2f2ed, // its own insignia white
    props: [{ node: "Propeller", dir: 1 }],
    // Six fifties; the quad battery flashes the outer two pairs.
    muzzles: [[1.21, -0.14, 2.8], [1.21, -0.14, -2.8], [1.18, -0.11, 3.05], [1.18, -0.11, -3.05]],
    trails: { nacelles: 1.8, tip: 5.7 },
  },
  // The Corsair is painted too (art/liverylib_f4u): its sea blue is split into
  // uppers, flanks, belly and fin, and the white cowl ring, wingtips and
  // rudder are marks on those surfaces, so every scheme repaints it. The
  // factory finish is plain Glossy Sea Blue and carries no decals; the PAINT
  // SHOP's three plates (Kepford's No. 29, Vaughan's F4U-2, Cawley's F4U-5NL)
  // bring their own numbers, letters and radar pods. The trim is left for
  // the wreck's char.
  f4u: {
    prefix: "F4U_",
    trim: 0xe8e6dc,
    livery: "Factory",
    props: [{ node: "Propeller", dir: 1 }],
    // Six fifties out past the gull's knee; the quad flashes the inner pairs.
    muzzles: [[1.31, -0.85, 3.3], [1.31, -0.85, -3.3], [1.28, -0.81, 3.55], [1.28, -0.81, -3.55]],
    trails: { nacelles: 1.8, tip: 6.28 },
  },
  bf110: {
    prefix: "B110_",
    trim: 0xe8e6dc,
    props: [{ node: "Propeller_L", dir: -1 }, { node: "Propeller_R", dir: 1 }],
    // The nose battery's centre, then the belly cannon.
    muzzles: [[4.75, 0.26, 0], [4.0, -0.45, 0.14], [4.0, -0.45, -0.14]],
    trails: { nacelles: 2.6, tip: 8.12 },
    // The rear gunner's twin MG 81Z, built facing aft; the tips are its
    // FX_Muzzle_RearGun empties (model-integration.md 1a).
    turrets: [{ node: "RearGun", aft: true, tip: [1.0, 0.1, 0.06] }],
  },
  a20: {
    prefix: "A20_",
    trim: 0xe8e6dc,
    props: [{ node: "Propeller_L", dir: -1 }, { node: "Propeller_R", dir: 1 }],
    // The gun nose's six fifties fire as one centerline stream.
    muzzles: [[6.65, 0, 0], [6.65, 0.18, 0.12], [6.65, 0.18, -0.12]],
    trails: { nacelles: 2.85, tip: 9.38 },
    // Crew seats in config order: the Martin power turret (built over the
    // nose, like the Fortress's top turret), then the tunnel gun (built aft).
    turrets: [
      { node: "TopTurret", aft: false, tip: [1.35, 0.28, 0.12] },
      { node: "VentralGun", aft: true, tip: [0.95, -0.05, 0] },
    ],
  },
  ju88: {
    prefix: "J88_",
    trim: 0xe8e6dc,
    props: [{ node: "Propeller_L", dir: -1 }, { node: "Propeller_R", dir: 1 }],
    muzzles: [[6.3, 0.2, -0.12]], // the windscreen MG
    trails: { nacelles: 2.65, tip: 10.06 },
    // The B-stand behind the cockpit and the Bola's gun, both built aft.
    turrets: [
      { node: "RearGun", aft: true, tip: [0.9, 0.12, 0.06] },
      { node: "VentralGun", aft: true, tip: [0.7, 0, 0] },
    ],
  },
  he111: {
    prefix: "H111_",
    trim: 0xe8e6dc,
    props: [{ node: "Propeller_L", dir: -1 }, { node: "Propeller_R", dir: 1 }],
    muzzles: [[7.75, 0.1, 0]], // the nose MG in the glazing
    trails: { nacelles: 3.2, tip: 11.42 },
    // The dorsal hood's gun and the Bola's, both built aft.
    turrets: [
      { node: "TopGun", aft: true, tip: [1.0, 0.15, 0] },
      { node: "VentralGun", aft: true, tip: [0.8, 0, 0] },
    ],
  },
  me262: {
    prefix: "M262_",
    trim: 0xe8e6dc,
    props: [],
    // Two Jumo 004s: all turbine to the ear, but no afterburner to light —
    // the plume stays dark, like the B-52's pods.
    jet: true,
    nozzles: [],
    // The four MK 108s in the nose, centred for the class's single stream.
    muzzles: [[4.75, 0.15, 0], [4.75, 0.25, 0.12], [4.75, 0.25, -0.12], [4.75, 0.05, 0.2]],
    trails: { nacelles: 2.0, tip: 6.26 },
  },
  // --- the jets era (F-104 / MiG-21 / F-4 / B-52) ----------------------------
  mig21: {
    prefix: "M21_",
    trim: 0xe8e6dc, // its own white
    props: [],
    jet: true,
    nozzles: [[-7.6, 0.1, 0]],
    muzzles: [[3.2, -0.55, 0.5]],
    trails: { nacelles: 0.6, tip: 3.62 },
  },
  f4_phantom: {
    prefix: "F4_",
    trim: 0xe8e6dc, // its own white
    props: [],
    jet: true,
    nozzles: [[-8.7, -0.1, 0.42], [-8.7, -0.1, -0.42]],
    muzzles: [[6.8, -0.7, 0]],
    trails: { nacelles: 0.42, tip: 6.5 },
  },
  b52: {
    prefix: "B52_",
    trim: 0xe8e6dc, // insignia white
    props: [],
    jet: true, // eight podded turbojets: all sound, no burner plume
    nozzles: [],
    muzzles: [[23.4, -0.5, 0.35], [23.4, -0.5, -0.35]],
    // Eight turbojets in four twin pods: a contrail off each pod's pair.
    trails: { nacelles: 9.0, outboard: 17.0, tip: 27.2 },
    turrets: [{ node: "TailTurret", aft: true, tip: [2.4, 0.15, 0.2] }],
  },
};

// The Spitfire Mk XVI's clipped wing (flightsim-71o): the Mk IX's own art, cut
// square at its outboard station 5.0 m out, where the elliptical tip begins
// to curl in -- a 10 m span to the IX's 11.2, near enough the real XVI's 9.9
// on this airframe's scale. Nothing is re-drawn: every level loads the IX's
// file (`base`) and models.js folds whatever lies past `clipSpan` flat onto
// that station, so the tip's own skin becomes the squared cap and the decals
// stop at it. The office, the liveries and the PAINT SHOP's YQ-P are the
// Spitfire's (`modelFamily`); only the outline, its contrails and the
// silhouette rounds strike (hitbox.go `spitfire16`) are the XVI's own. The
// roster names it through the type's `airframe` key (planes.js applyRoster).
AIRCRAFT_MODELS.spitfire16 = {
  ...AIRCRAFT_MODELS.spitfire,
  base: "spitfire",
  clipSpan: 5.0,
  trails: { nacelles: 1.8, tip: 5.0 },
};

/** The model a stem's office, liveries and paint schemes belong to: its
 * `base` for a re-cut airframe (the Mk XVI's is the Spitfire's), else itself. */
export function modelFamily(stem) {
  return AIRCRAFT_MODELS[stem]?.base ?? stem;
}

// Class index → model (roster A: one airframe per class, both teams). The
// first eight are the original roster and the ad circus; the rest of the
// hangar joined after them, so no saved pick or career ledger changed hands.
export const CLASS_MODEL = [
  "spitfire", "p51d", "p38", "b17", "tbf_avenger", "f104", "skywriter", "marquee",
  "bf109", "fw190", "p40n", "f4u", "bf110", "a20", "ju88", "he111", "me262",
];

// Era roster `model` keys (docs/early-jets-era.md) → model.
export const ERA_MODEL = { starfang: "f104", balalaika: "mig21", rhino: "f4_phantom", arclight: "b52" };

// Level-of-detail distances (Part 3.1): LOD1 past 250 m, LOD2 past 700 m, and
// past 2 km the old box silhouette in the same factory finish, one draw call.
export const AIRCRAFT_LOD = [250, 700, 2000];

// Every level of an airframe paints teamless in its trim — the damage kit's
// holed and torn panels and the flightline hulk (`_wreck`) with it. (The ad
// circus carries no team marks at all: its carnival livery is its own.)
for (const [stem, spec] of Object.entries(AIRCRAFT_MODELS)) {
  if (spec.trim == null) continue;
  for (const suf of ["", "_lod1", "_lod2", "_damage", "_wreck"]) {
    // The decal-less levels paint a camouflage as its mix (models.js camoOf).
    const far = suf === "_lod1" || suf === "_lod2";
    const opts = { trim: spec.trim, livery: spec.livery ?? null, camo: far ? (spec.camo ?? null) : null };
    // A re-cut airframe loads its base's files, every level clipped but the
    // flightline hulk: a burnt-out wreck's wings lie where they fell.
    if (spec.base) Object.assign(opts, { source: spec.base + suf, clipSpan: suf === "_wreck" ? null : spec.clipSpan });
    setModelOptions(stem + suf, opts);
  }
}

/** The model spec flying class `type`, or null (boxes only). */
export function modelForClass(type, classModels = CLASS_MODEL) {
  if (!modelsEnabled()) return null;
  const stem = classModels[type];
  return stem && AIRCRAFT_MODELS[stem] ? { stem, ...AIRCRAFT_MODELS[stem] } : null;
}

// --- anchors ---------------------------------------------------------------
// The box era hand-typed every damage anchor (DAMAGE_ANCHORS in planes.js).
// For a model they fall out of its node bounds: a scorch, a kill-mark stencil
// or a shed wingtip lands on the skin you can see.
const anchorCache = new Map(); // stem → anchors

export function anchorsFor(stem, template) {
  let a = anchorCache.get(stem);
  if (a) return a;
  const b = (n) => template.bounds(n);
  const wing = b("Wing_R") ?? new THREE.Box3(new THREE.Vector3(-1, -0.4, 0), new THREE.Vector3(1, 0.2, 5));
  const fuse = b("Fuselage") ?? b("Gondola") ?? b("Envelope") ?? wing;
  const canopy = b("Canopy") ?? b("CanopyFrame");
  const tailR = b("Tailplane_R");
  const fin = b("Fin") ?? b("Fin_R");
  const spec = AIRCRAFT_MODELS[stem] ?? {};
  const prop0 = spec.props?.[0] ? template.node(spec.props[0].node) : null;
  const nozzle = spec.nozzles?.[0];
  const fz = (fuse.max.z - fuse.min.z) / 2;
  const fh = fuse.max.y - fuse.min.y;
  // The engine the scorch and the ace's gold spinner find: just behind the
  // (first) prop hub, or the jet's nozzle.
  let engine;
  if (prop0) {
    const p = prop0.position;
    engine = { x: p.x - 0.8, y: p.y, z: p.z, r: Math.max(0.5, fh * 0.45) };
  } else if (nozzle) {
    engine = { x: nozzle[0] + 0.8, y: nozzle[1], z: nozzle[2], r: Math.max(0.45, fh * 0.35) };
  } else {
    engine = { x: fuse.max.x * 0.6, y: (fuse.min.y + fuse.max.y) / 2, z: 0, r: fh * 0.4 };
  }
  a = {
    engine,
    wing: {
      x: (wing.min.x + wing.max.x) / 2,
      y: (wing.min.y + wing.max.y) / 2,
      tipZ: wing.max.z,
      chord: (wing.max.x - wing.min.x) * 0.5,
      thick: Math.max(0.12, (wing.max.y - wing.min.y) * 0.4),
    },
    fuse: {
      x0: fuse.min.x * 0.45,
      x1: fuse.max.x * 0.45,
      y: (fuse.min.y + fuse.max.y) / 2 + fh * 0.15,
      z: fz,
      h: fh * 0.6,
    },
    canopy: canopy
      ? { x: (canopy.min.x + canopy.max.x) / 2, y: canopy.max.y - 0.2, w: canopy.max.x - canopy.min.x, d: canopy.max.z - canopy.min.z }
      : { x: fuse.max.x * 0.2, y: fuse.max.y, w: 1.2, d: 0.8 },
    tail: tailR
      ? { x: (tailR.min.x + tailR.max.x) / 2, y: (tailR.min.y + tailR.max.y) / 2, spanZ: tailR.max.z, finY: fin ? fin.max.y : tailR.max.y + 1 }
      : { x: fuse.min.x + 1, y: (fuse.min.y + fuse.max.y) / 2, spanZ: fz * 1.5, finY: fuse.max.y + 0.5 },
    // The fin(s), where a scheme's stripe paints and the ace pennant flies.
    fins: [b("Fin"), b("Fin_L"), b("Fin_R")].filter(Boolean).map((f) => ({
      x: f.max.x - (f.max.x - f.min.x) * 0.28,
      y: f.min.y + (f.max.y - f.min.y) * 0.62,
      z: (f.min.z + f.max.z) / 2,
      w: (f.max.x - f.min.x) * 0.3,
      h: (f.max.y - f.min.y) * 0.22,
      d: Math.max(0.12, f.max.z - f.min.z) + 0.06,
      top: f.max.y,
    })),
  };
  anchorCache.set(stem, a);
  return a;
}

// --- the damage kit (Part 4.4) -------------------------------------------------

const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
const sev = (h, start) => clamp01((start - (h ?? 1)) / start);

// Tiny deterministic PRNG + hole seed — the SAME functions planes.js uses, so a
// hole lands identically on every client and in the killcam (roadmap #8).
function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function holeSeed(id, comp, i) {
  let h = ((id >>> 0) ^ 0x9e3779b1) | 0;
  h = Math.imul(h ^ (comp + 1), 0x85ebca77);
  h = Math.imul(h ^ (i + 1), 0xc2b2ae3d);
  return (h ^ (h >>> 15)) >>> 0;
}

// Components in wire order (0 airframe · 1 engine · 2 wingL · 3 wingR · 4 fuel ·
// 5 pilot · 6 tail) and the skin samples each draws its holes from.
const HOLE_COMPS = ["fuse", "engine", "wingL", "wingR", "fuel", "pilot", "tail"];
const HOLES = 7; // per subsystem at zero health
const HP_HOLES = 4; // the airframe's cap: broad, but tough

// Hole decal kinds: the MG pock, the cannon punch and the flak tear, sized as
// the art asks (unit discs, scaled to the round). Which one a hole is rides the
// same seeded stream as where it is.
const HOLE_KINDS = [
  { stem: "bullet_hole", min: 0.12, max: 0.2 },
  { stem: "cannon_hole", min: 0.25, max: 0.4 },
  { stem: "flak_hole", min: 0.4, max: 0.7 },
];
const HOLE_CAP = HOLES * 6 + HP_HOLES;

const _up = new THREE.Vector3(0, 1, 0);
const _q = new THREE.Quaternion();
const _qs = new THREE.Quaternion();
const _p = new THREE.Vector3();
const _n = new THREE.Vector3();
const _s = new THREE.Vector3();
const _m = new THREE.Matrix4();

/**
 * The model-side half of a PlaneVisual: three LOD instances of one airframe,
 * their propellers and turret nodes, the damage kit, and the hole decals.
 * Built only once every level it needs has loaded.
 */
export class AircraftRig {
  /**
   * @param spec    AIRCRAFT_MODELS entry plus { stem }
   * @param levels  [Template lod0, Template|null lod1, Template|null lod2]
   * @param paint   { team, scheme }  (scheme = { body, accent } or null)
   * @param limits  { WING_LOSE, TAIL_LOSE, CANOPY_LOSE } from planes.js
   */
  constructor(spec, levels, paint, limits) {
    this.spec = spec;
    this.limits = limits;
    this.levels = levels.filter(Boolean).map((t) => ({ base: new ModelInstance(t, paint), kit: null }));
    this.anchors = anchorsFor(spec.stem, levels[0]);
    this.team = paint.team;
    this.schemeDef = paint.scheme;
    this.kit = null; // the damage-kit JSON once it loads
    this.kitRequested = false;
    this.props = this.levels.map((l) => spec.props.map((p) => ({ node: l.base.node(p.node), dir: p.dir })).filter((p) => p.node));
    this.turrets = this.levels.map((l) =>
      (spec.turrets ?? []).map((t) => ({ spec: t, node: l.base.node(t.node) })).filter((t) => t.node),
    );
    // A livery's tail decals (the rudder's tallies, the camouflage on the fin
    // and tailplanes) go with the tail, and its outer-wing decals with the wing.
    this.liveryTails = this.levels.map((l) => l.base.nodes(/^Livery_[A-Za-z]+_Tail$/));
    this.liveryWings = this.levels.map((l) => [
      l.base.nodes(/^Livery_[A-Za-z]+_Wing_L$/),
      l.base.nodes(/^Livery_[A-Za-z]+_Wing_R$/),
    ]);
    // Past LOD0 the airframe draws its rigid parts as one batch (models.js
    // flatten): a fighter at LOD2 goes from eight draws to two, the prop and
    // the rest. The props and turrets stay their own, since they move; a
    // wing shot away puts the parts back (applyDamage). LOD0 is the one the
    // cockpit, the stripe, the holes and the debris reach into, and it stays
    // as built.
    for (let li = 1; li < this.levels.length; li++) {
      this.levels[li].base.flatten([...this.props[li].map((p) => p.node), ...this.turrets[li].map((t) => t.node)]);
    }
    this.holes = null; // { kind → { paint: InstancedMesh, soot: InstancedMesh } }
    this.holeSig = "";
    this.lastLost = null;
    // Prop hub positions and radii for the blur discs, off LOD0's nodes.
    this.levels[0]?.base.root.updateMatrixWorld(true);
    this.propDiscs = (this.props[0] ?? []).map(({ node }) => {
      const box = new THREE.Box3().setFromObject(node);
      const size = box.getSize(new THREE.Vector3());
      node.updateMatrix();
      return { pos: node.position.clone(), r: Math.max(0.6, Math.max(size.y, size.z) / 2), hub: box.max.x - node.position.x };
    });
  }

  /** Objects for setLodLevels: every model level, then the box level at range. */
  lodLevels(boxLevel) {
    const d = AIRCRAFT_LOD;
    const out = this.levels.map((l, i) => [l.base.root, i === 0 ? 0 : d[i - 1]]);
    out.push([boxLevel, d[2]]);
    return out;
  }

  /** Repaint every level (a scheme swap). */
  paint(team, scheme) {
    this.team = team;
    this.schemeDef = scheme;
    for (const l of this.levels) {
      l.base.paint(team, scheme);
      l.kit?.paint(team, scheme);
    }
  }

  /** Advance every propeller (rate in rad/s; handed twins counter-rotate). */
  spin(dt, rate) {
    for (const lvl of this.props) for (const p of lvl) p.node.rotation.x += dt * rate * p.dir;
  }

  /** Pose crew turret `seat` on every level (body-frame yaw 0 = over the nose). */
  poseTurret(seat, yaw, pitch) {
    for (const lvl of this.turrets) {
      const t = lvl[seat];
      if (!t) continue;
      // Built-aft turrets hold yaw 0 as "pointing aft".
      t.node.rotation.y = t.spec.aft ? yaw - Math.PI : yaw;
      // Domes and cradles carry their barrels baked in: tilt them only as far
      // as reads as aiming (the muzzle flash rides the true line — see
      // PlaneVisual's turret flashes). The tilt is about the node's own Z,
      // applied before its yaw, so a built-aft mount's barrels (down −X) lift
      // on a NEGATIVE turn: the same sign there sank the B-17's stinger
      // muzzles to the ground as its gunner aimed up.
      const tilt = Math.max(-0.35, Math.min(0.35, pitch));
      t.node.rotation.z = t.spec.aft ? -tilt : tilt;
    }
  }

  /** Ask for the damage kit (idempotent). Resolves when it's mounted. */
  requestKit() {
    if (this.kitRequested) return this._kitP;
    this.kitRequested = true;
    const stem = this.spec.stem;
    this._kitP = Promise.all([loadModel(`${stem}_damage`), loadDamageKit(stem)])
      .then(([tpl, json]) => {
        if (this.disposed) return;
        this.kit = json;
        // LOD0 and LOD1 wear the kit (Part 4.4: at LOD1 keep the swaps and the
        // scorch, drop the holes; LOD2 only hides what's lost).
        for (let i = 0; i < Math.min(2, this.levels.length); i++) {
          const k = new ModelInstance(tpl, { team: this.team, scheme: this.schemeDef });
          // Every kit node starts hidden; per-plane overlay materials so one
          // plane's scorch doesn't blacken the squadron.
          for (const [name, node] of k._nodes) {
            if (node.parent?.parent === k.root) node.visible = false;
            if (/Scorch|FuelStain/.test(name)) {
              node.traverse((o) => {
                if (o.isMesh) o.material = o.material.clone();
              });
            }
          }
          this.levels[i].base.root.add(k.root);
          this.levels[i].kit = k;
        }
        this.onKit?.(); // the owner re-applies its damage state onto the kit
      })
      .catch(() => {});
    return this._kitP;
  }

  _node(level, name) {
    return level.base.node(name) ?? level.kit?.node(name) ?? null;
  }

  /**
   * Show a component-damage state: d = { engine, wingL, wingR, fuel, pilot,
   * tail, hp } in 0..1. `lost` = { wingL, wingR, canopy, tail } from planes.js.
   * Returns the list of sections that tore off on THIS call (for debris).
   */
  applyDamage(d, lost, entityId) {
    const L = this.limits;
    const any = (d.engine ?? 1) < 0.999 || (d.wingL ?? 1) < 0.999 || (d.wingR ?? 1) < 0.999 ||
      (d.fuel ?? 1) < 0.999 || (d.pilot ?? 1) < 0.999 || (d.tail ?? 1) < 0.999 || (d.hp ?? 1) < 0.999;
    if (any && !this.kitRequested) this.requestKit();
    const kit = this.kit;
    // The batch can stand in only for parts that all show as built: LOD2
    // draws no scorch or swaps, so there it lasts until a section goes; LOD1
    // wears the kit, so there the first scratch hands it back to the parts.
    const whole = !lost.wingL && !lost.wingR && !lost.tail && !lost.canopy;
    this.levels.forEach((lvl, li) => {
      if (li >= 1) lvl.base.setFlat(li === 1 ? whole && !any : whole);
      const set = (name, v) => {
        const n = this._node(lvl, name);
        if (n) n.visible = v;
      };
      for (const n of this.liveryTails[li]) n.visible = !lost.tail;
      for (const n of this.liveryWings[li][0]) n.visible = !lost.wingL;
      for (const n of this.liveryWings[li][1]) n.visible = !lost.wingR;
      if (kit && lvl.kit) {
        // Reset every node the kit manages to its pristine state, then apply
        // the current damage — idempotent, so a deck repair un-shreds a wing.
        const sect = [kit.wing?.left, kit.wing?.right, kit.tail, kit.canopy].filter(Boolean);
        for (const s of sect) {
          for (const [base, dmg] of Object.entries(s.swapDamaged ?? {})) { set(base, true); set(dmg, false); }
          for (const [base, stub] of Object.entries(s.swapLost ?? {})) { set(base, true); set(stub, false); }
          for (const n of s.hideOnLose ?? []) if (!/Scorch|_Damaged$|_Stub$|_Shattered$/.test(n)) set(n, true);
        }
        const section = (s, h, damagedBelow, isLost) => {
          if (!s) return;
          const damaged = !isLost && h < damagedBelow;
          for (const [base, dmg] of Object.entries(s.swapDamaged ?? {})) { set(base, !damaged && !isLost); set(dmg, damaged); }
          for (const [, stub] of Object.entries(s.swapLost ?? {})) set(stub, isLost);
          if (isLost) for (const n of s.hideOnLose ?? []) set(n, false);
        };
        section(kit.wing?.left, d.wingL ?? 1, 0.45, lost.wingL);
        section(kit.wing?.right, d.wingR ?? 1, 0.45, lost.wingR);
        section(kit.tail, d.tail ?? 1, 0.45, lost.tail);
        section(kit.canopy, d.pilot ?? 1, 0.45, lost.canopy);
        const scorch = (suffix, v) => {
          const n = lvl.kit.node(suffix);
          if (!n) return;
          n.visible = v > 0.02;
          n.traverse((o) => {
            if (o.isMesh) o.material.opacity = 0.92 * v;
          });
        };
        scorch("Scorch_Engine", sev(d.engine, 0.78));
        scorch("Scorch_Fuse", sev(d.hp, 0.8));
        scorch("FuelStain", sev(d.fuel, 0.85));
        scorch("Scorch_Wing_L", lost.wingL ? 0 : sev(d.wingL, 0.7));
        scorch("Scorch_Wing_R", lost.wingR ? 0 : sev(d.wingR, 0.7));
        scorch("Scorch_Tail", lost.tail ? 0 : sev(d.tail, 0.7));
      } else {
        // No kit (yet, or LOD2): simply hide what's gone.
        set("Wing_L", !lost.wingL);
        set("Wing_R", !lost.wingR);
        for (const n of ["Tailplane_L", "Tailplane_R", "Fin", "Fin_L", "Fin_R"]) set(n, !lost.tail);
        for (const n of ["Canopy", "CanopyFrame"]) set(n, !lost.canopy);
        if (li >= 2 || !kit) {
          // Hanging pods and tips go with their wing.
          for (const s of ["L", "R"]) {
            const gone = s === "L" ? lost.wingL : lost.wingR;
            for (const n of [`TipTank_${s}`, `WingTip_${s}`, `Missile_${s}`, `UpperWing_${s}`]) set(n, !gone);
          }
        }
      }
    });
    this._holes(d, lost, entityId);
  }

  // Bullet holes as decals on the real skin (Part 4.4): the same deterministic
  // (entity, component, index) stream as the box era, placed on the kit's
  // area-weighted skin samples instead of inside a hand-typed box.
  _holes(d, lost, entityId) {
    const kit = this.kit;
    const lvl = this.levels[0];
    if (!kit?.samples || !lvl) return;
    const health = [d.hp, d.engine, d.wingL, d.wingR, d.fuel, d.pilot, d.tail].map((v) => clamp01(v ?? 1));
    const counts = health.map((h, c) => Math.floor((1 - h) * (c === 0 ? HP_HOLES : HOLES)));
    const sig = `${counts.join(".")}|${lost.wingL ? 1 : 0}${lost.wingR ? 1 : 0}`;
    if (sig === this.holeSig) return;
    this.holeSig = sig;
    const total = counts.reduce((a, b) => a + b, 0);
    if (!total && !this.holes) return;
    if (!this.holes) {
      if (!this._holeKinds) {
        this._holeKinds = true;
        for (const k of HOLE_KINDS) loadModel(k.stem).catch(() => {});
      }
      const tpls = HOLE_KINDS.map((k) => modelIfReady(k.stem));
      if (tpls.some((t) => !t)) {
        // Decals still in flight: try again on the next damage change.
        this.holeSig = "";
        Promise.all(HOLE_KINDS.map((k) => loadModel(k.stem))).then(() => {
          if (!this.disposed) this._holes(d, lost, entityId);
        }, () => {});
        return;
      }
      this.holes = HOLE_KINDS.map((k, i) => {
        const root = tpls[i].root;
        const meshes = [];
        root.traverse((o) => {
          if (o.isMesh) meshes.push(o);
        });
        return meshes.map((src) => {
          const im = new THREE.InstancedMesh(src.geometry, src.material, HOLE_CAP);
          im.count = 0;
          im.frustumCulled = false; // instances span the airframe, not the origin
          im.renderOrder = src.renderOrder;
          lvl.base.root.add(im);
          return im;
        });
      });
    }
    const break_ = (side) => (kit.wing?.[side]?.breakAt ?? Infinity);
    const used = HOLE_KINDS.map(() => 0);
    for (let c = 0; c < 7; c++) {
      const samples = kit.samples[HOLE_COMPS[c]];
      if (!samples?.length) continue;
      // A torn-away empennage or blown-off canopy takes its holes with it.
      if ((c === 6 && lost.tail) || (c === 5 && lost.canopy)) continue;
      for (let i = 0; i < counts[c]; i++) {
        const r = mulberry32(holeSeed(entityId, c, i));
        const s = samples[Math.floor(r() * samples.length)];
        // Skip holes out on a wing that isn't there any more.
        if (lost.wingL && s[2] < -break_("left")) continue;
        if (lost.wingR && s[2] > break_("right")) continue;
        // Mostly MG pocks, a cannon punch in four, the odd flak tear — the
        // airframe (flak's favourite target) tears a little more often.
        const roll = r();
        const k = c === 0 ? (roll < 0.1 ? 2 : roll < 0.38 ? 1 : 0) : roll < 0.05 ? 2 : roll < 0.27 ? 1 : 0;
        const kind = HOLE_KINDS[k];
        const size = kind.min + r() * (kind.max - kind.min);
        _n.set(s[3], s[4], s[5]).normalize();
        _p.set(s[0], s[1], s[2]).addScaledVector(_n, 0.01);
        _q.setFromUnitVectors(_up, _n);
        _qs.setFromAxisAngle(_up, r() * Math.PI * 2);
        _q.multiply(_qs);
        _s.setScalar(size);
        _m.compose(_p, _q, _s);
        for (const im of this.holes[k]) im.setMatrixAt(used[k], _m);
        used[k]++;
      }
    }
    this.holes.forEach((ims, k) => {
      for (const im of ims) uploadInstances(im, used[k]);
    });
  }

  /**
   * World-space debris for a section that just tore off: a painted clone of
   * the torn piece, wrapped so it tumbles about its own centre. `kind` is
   * wingL / wingR / tail / canopy.
   */
  debrisFor(kind, groupMatrixWorld) {
    const lvl = this.levels[0];
    const kit = this.kit;
    let names;
    if (kind === "wingL" || kind === "wingR") {
      names = kit?.wing?.[kind === "wingL" ? "left" : "right"]?.debris ?? [];
    } else if (kind === "tail") {
      names = kit?.tail?.debris ?? ["Tailplane_L", "Tailplane_R", "Fin", "Fin_L", "Fin_R"];
    } else {
      names = kit?.canopy?.debris ?? ["Canopy"];
    }
    const pieces = names.map((n) => this._node(lvl, n)).filter(Boolean);
    if (!pieces.length) return null;
    const box = new THREE.Box3();
    lvl.base.root.updateMatrixWorld(true);
    // Bounds in model space: undo the plane's world transform.
    const inv = new THREE.Matrix4().copy(lvl.base.root.matrixWorld).invert();
    for (const p of pieces) {
      p.traverse((o) => {
        if (o.isMesh) {
          o.geometry.boundingBox ?? o.geometry.computeBoundingBox();
          const bb = o.geometry.boundingBox.clone().applyMatrix4(new THREE.Matrix4().multiplyMatrices(inv, o.matrixWorld));
          box.union(bb);
        }
      });
    }
    const center = box.getCenter(new THREE.Vector3());
    const wrap = new THREE.Group();
    for (const p of pieces) {
      const c = p.clone(true);
      c.visible = true;
      c.traverse((o) => {
        o.visible = true;
      });
      // p's model-space matrix, re-expressed about the piece's centre.
      const local = new THREE.Matrix4().multiplyMatrices(inv, p.matrixWorld);
      local.premultiply(new THREE.Matrix4().makeTranslation(-center.x, -center.y, -center.z));
      local.decompose(c.position, c.quaternion, c.scale);
      wrap.add(c);
    }
    const world = new THREE.Matrix4().multiplyMatrices(groupMatrixWorld, new THREE.Matrix4().makeTranslation(center.x, center.y, center.z));
    world.decompose(wrap.position, wrap.quaternion, wrap.scale);
    return wrap;
  }

  dispose() {
    this.disposed = true;
    for (const ims of this.holes ?? []) for (const im of ims) im.dispose();
    for (const l of this.levels) {
      l.kit?.root.traverse((o) => {
        if (o.isMesh && /Scorch|FuelStain/.test(o.material?.name ?? "")) o.material.dispose();
      });
    }
  }
}

/** The fin-stripe boxes a scheme paints (planes.js mergeBoxes format). */
export function finStripeBoxes(anchors, color) {
  return anchors.fins.map((f) => ({ x: f.x, y: f.y, z: f.z, w: f.w, h: f.h, d: f.d, color }));
}

/** Start fetching every level of a class's model (warm the cache at join). */
export function prefetchAircraft(stem) {
  if (!modelsEnabled() || !AIRCRAFT_MODELS[stem]) return;
  for (const s of [stem, `${stem}_lod1`, `${stem}_lod2`]) loadModel(s).catch(() => {});
}

export { paintMaterialDS };
